import React, { useState } from "react";
import {
  Grid,
  TextField,
  Box,
  Typography,
  createStyles,
  withStyles,
  Button,
} from "@material-ui/core";
import { formatCurrency } from "../../helpers/currency";

// Partner balances (and `kycDeduction`) are stored as currency amount x 100000.
const BALANCE_UNIT = 100000;

// Mirrors BVBP_FEE_BY_CURRENCY in whatsapp-rest-api/src/helpers/kycPricing.js —
// what a partner is charged when `kycDeduction` is not set.
const DEFAULT_KYC_FEES = {
  INR: { selfCertify: 100, indiaKyc: 150 },
  USD: { selfCertify: 3, indiaKyc: 3 },
};

/**
 * @typedef {import("../../types").Partner} Partner
 */

/**
 * KYC / PLBV fee for a partner, stored in `partner.kycDeduction`. When set it
 * replaces the default fee for every business verification flow; when unset
 * the default pricing in whatsapp-rest-api (helpers/kycPricing) applies.
 *
 * @param {{
 *   partner: Partner,
 *   updateAttribute: (key: string, value: any) => Promise<void>,
 *   showErrorMessage: (msg: string) => void,
 * } & import("@material-ui/core").WithStyles<typeof styles>} props
 */
function PartnerKycFeeInput({
  classes,
  partner,
  updateAttribute,
  showErrorMessage,
}) {
  const currency = String(partner?.currency || "INR").toUpperCase();
  const hasFee = typeof partner?.kycDeduction === "number";
  const currentFee = hasFee ? partner.kycDeduction / BALANCE_UNIT : null;
  const defaults = DEFAULT_KYC_FEES[currency] || DEFAULT_KYC_FEES.INR;

  const [fee, setFee] = useState(hasFee ? String(currentFee) : "");

  const updateFee = async () => {
    const value = Number(fee);
    if (fee === "" || !Number.isFinite(value) || value < 0) {
      showErrorMessage("Enter a valid KYC fee (0 or more)");
      return;
    }
    await updateAttribute("kycDeduction", Math.round(value * BALANCE_UNIT));
  };

  const resetFee = async () => {
    await updateAttribute("kycDeduction", null);
    setFee("");
  };

  return (
    <Box>
      <Box my={6}></Box>
      <Typography variant="body1" gutterBottom style={{ fontWeight: "bold" }}>
        KYC Fee (PLBV) - Charged once per project on business verification
      </Typography>
      <Typography variant="body1" gutterBottom>
        {`Current KYC Fee - ${
          hasFee ? formatCurrency(currency, currentFee) : ""
        }`}
      </Typography>
      <Typography variant="body1" gutterBottom>
        {`Default KYC Fee - ${formatCurrency(
          currency,
          defaults.selfCertify
        )} (self-certify) / ${formatCurrency(
          currency,
          defaults.indiaKyc
        )} (Aadhaar / GST KYC)`}
      </Typography>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={6}>
          <TextField
            type="number"
            value={fee}
            placeholder={`Fee in ${currency}`}
            inputProps={{ min: 0, step: "0.01" }}
            onChange={(e) => setFee(e.target.value)}
            className={classes.textField}
            fullWidth
          />
        </Grid>
        <Grid item>
          <Button variant="contained" color="primary" onClick={updateFee}>
            Update KYC Fee
          </Button>
        </Grid>
        {hasFee && (
          <Grid item>
            <Button variant="outlined" color="primary" onClick={resetFee}>
              Reset to Default
            </Button>
          </Grid>
        )}
      </Grid>
    </Box>
  );
}

const styles = () =>
  createStyles({
    textField: {
      backgroundColor: "white",
      border: "1px solid black",
    },
  });

export default withStyles(styles)(PartnerKycFeeInput);
